import axiosInstance from '../../../store/api';

const BASE = '/config/retail-pos';
const UPLOAD_URL = `${BASE}/upload-imagen`;

export const retailPosApi = {
  // Fetch a single entity's data. Backend routes by X-Entidad header.
  getEntity: (entidad) =>
    axiosInstance
      .get(BASE, { headers: { 'X-Entidad': entidad } })
      .then((r) => r.data),

  // Legacy SYNC — kept for backward compat if needed.
  sync: () => axiosInstance.post(BASE, { type: 'SYNC' }).then((r) => r.data),

  transact: ({ transactionId, action, payload, clientTimestamp }) =>
    axiosInstance
      .post(BASE, {
        type: 'TRANSACTION',
        transactionId,
        action,
        payload,
        clientTimestamp: clientTimestamp || new Date().toISOString(),
      })
      .then((r) => r.data),

  // Upload a product image. Returns { ok, url, key, sizeBytes, storage }.
  uploadProductImage: (file) => {
    const form = new FormData();
    form.append('imagen', file);
    return axiosInstance
      .post(UPLOAD_URL, form, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((r) => r.data);
  },

  // Fetch current storage usage for billing display.
  getStorageUsage: () =>
    axiosInstance
      .get(BASE, { headers: { 'X-Entidad': 'storage' } })
      .then((r) => r.data),

  getTiendaActual: () =>
    axiosInstance.get('/negocio/tienda-actual').then((r) => r.data),

  updateTiendaActual: (payload) =>
    axiosInstance.patch('/negocio/tienda-actual', payload).then((r) => r.data),
};

export const isOfflineError = (error) =>
  error?.code === 'ERR_NETWORK_OFFLINE' ||
  (!error?.response && (error?.message === 'Network Error' || !navigator.onLine));

export const isSubscriptionDeniedError = (error) => {
  const status = error?.response?.status;
  const message = String(error?.response?.data?.message || '').toLowerCase();
  return status === 403 && message.includes('suscripcion') && message.includes('vencid');
};

export const isRecoverableSyncError = (error) => {
  const status = error?.response?.status;
  const message = String(
    error?.response?.data?.message || error?.message || ''
  ).toLowerCase();

  // Mongo transient transaction failures can bubble up as 400 with
  // "Please retry your operation or multi-document transaction."
  const isTransientTxError =
    message.includes('please retry your operation') ||
    message.includes('multi-document transaction') ||
    message.includes('write conflict') ||
    message.includes('transient');

  const isRetryableHttp = status === 400 || status === 409 || status === 429 || status >= 500;
  return Boolean(isRetryableHttp && isTransientTxError);
};

// ---------------------------------------------------------------------------
// Per-entity cache  (replaces the old single-blob cache)
// Each entity is cached independently under retail_pos_v3_{entidad}
// ---------------------------------------------------------------------------

const CACHE_VERSION  = 'v3';
const SYNC_TTL_MS    = 5 * 60 * 1000; // 5 minutes
/** Intereses vienen del catálogo público; TTL más corto que el resto del POS. */
const ENTITY_TTL_MS = {
  'intereses-catalogo': 60 * 1000,
};

const getScope = () => {
  const rawUser = localStorage.getItem('user');
  const activeStoreId = String(localStorage.getItem('active_store_id') || '').trim();

  let userId = '';
  try {
    const user = rawUser ? JSON.parse(rawUser) : null;
    userId = String(user?._id || '').trim();
  } catch {
    userId = '';
  }

  return {
    userId: userId || 'anon',
    storeId: activeStoreId || 'no-store',
  };
};

const cacheKey = (entidad) => {
  const { userId, storeId } = getScope();
  return `retail_pos_${CACHE_VERSION}_${userId}_${storeId}_${entidad}`;
};

export const retailPosCache = {
  getEntity: (entidad) => {
    try {
      const raw = localStorage.getItem(cacheKey(entidad));
      return raw ? JSON.parse(raw).data ?? null : null;
    } catch {
      return null;
    }
  },

  setEntity: (entidad, data) => {
    try {
      localStorage.setItem(
        cacheKey(entidad),
        JSON.stringify({ data, cachedAt: Date.now() })
      );
    } catch {
      // localStorage full — silently ignore
    }
  },

  isEntityFresh: (entidad, maxAgeMs) => {
    const ttl = maxAgeMs ?? ENTITY_TTL_MS[entidad] ?? SYNC_TTL_MS;
    try {
      const raw = localStorage.getItem(cacheKey(entidad));
      if (!raw) return false;
      const { cachedAt } = JSON.parse(raw);
      return cachedAt ? Date.now() - cachedAt < ttl : false;
    } catch {
      return false;
    }
  },

  clearEntity: (entidad) => {
    localStorage.removeItem(cacheKey(entidad));
  },

  clearAll: (entityKeys) => {
    (entityKeys || []).forEach((e) => localStorage.removeItem(cacheKey(e)));
  },
};

